import React, { useState, useEffect } from 'react';
import { Search, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { contentService } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';

// Function to convert URLs in text to clickable links
const renderTextWithLinks = (text) => {
  if (!text) return null;
  
  // Regex to match URLs
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = text.split(urlRegex);
  
  return parts.map((part, index) => {
    if (part.match(urlRegex)) {
      return (
        <a
          key={index}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          className="text-teal-600 hover:text-teal-800 underline break-all"
        >
          {part}
        </a>
      );
    }
    return part;
  });
};

const FAQ = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [activeCategory, setActiveCategory] = useState('all');
  const [faqs, setFaqs] = useState([]);
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const { getText, isEnglish } = useLanguage();

  const categories = isEnglish ? [
    { id: 'all', label: 'All' },
    { id: 'general', label: 'General Questions' },
    { id: 'labor', label: 'Labor Law Questions' }
  ] : [
    { id: 'all', label: '全部' },
    { id: 'general', label: '常見問題' },
    { id: 'labor', label: '勞工法例問題' }
  ];

  // Default FAQs - Chinese
  const defaultFaqs = [
    { id: '1', category: 'general', question: '聘請外傭需要多少時間？', answer: '一般來說，聘請海外女傭需時約8-12星期，包括簽署合約、領事館公證、入境處批核及安排到港等程序。聘請完約現成女傭則較快，約4-6星期可完成。' },
    { id: '2', category: 'general', question: '聘請外傭有甚麼基本要求？', answer: '僱主必須是香港居民，擁有固定住址，並能提供獨立房間給外傭居住。僱主亦需有穩定收入，以支付外傭薪金及相關開支。' },
    { id: '3', category: 'cost', question: '聘請外傭的費用包括甚麼？', answer: '費用包括：驗身費、領事館合約認證、入境處簽證申請、機場接送（海外女傭）、兩年合約保證及免費更換服務。' },
    { id: '4', category: 'cost', question: '外傭的每月薪金是多少？', answer: '根據香港政府規定，2025年外傭最低工資為港幣$5,195。僱主亦需支付膳食津貼及提供免費住宿。' },
    { id: '5', category: 'process', question: '如何挑選合適的女傭？', answer: '我們會根據您的家庭需要，包括家庭成員、居住環境、特別要求等，為您配對最合適的女傭。您可透過履歷、相片及視像面試了解女傭。' },
    { id: '6', category: 'process', question: '如果女傭不合適可以更換嗎？', answer: '可以。我們提供兩年合約保證及免費更換服務。如女傭在試用期內表現不符期望，我們會免費安排更換。' },
    { id: '7', category: 'visa', question: '簽證申請需要多長時間？', answer: '入境處一般需要4-6星期處理簽證申請。加上領事館公證時間，整個簽證過程約需6-8星期。' },
    { id: '8', category: 'insurance', question: '需要為外傭購買甚麼保險？', answer: '根據法例，僱主必須為外傭購買僱員補償保險。我們建議同時購買醫療保險，以涵蓋門診及住院費用。' },
  ];

  // Default FAQs - English
  const defaultFaqsEn = [
    { id: '1', category: 'general', question: 'How long does it take to hire a domestic helper?', answer: 'Generally, hiring an overseas helper takes about 8-12 weeks, including contract signing, consulate authentication, immigration approval, and travel arrangements. Hiring a local available helper is faster, taking about 4-6 weeks.' },
    { id: '2', category: 'general', question: 'What are the basic requirements for hiring a domestic helper?', answer: 'The employer must be a Hong Kong resident with a fixed address and be able to provide a separate room for the helper. The employer must also have a stable income to pay the helper\'s salary and related expenses.' },
    { id: '3', category: 'cost', question: 'What does the hiring fee include?', answer: 'The fee includes: medical examination, consulate contract authentication, immigration visa application, airport pickup (for overseas helpers), two-year contract guarantee, and free replacement service.' },
    { id: '4', category: 'cost', question: 'What is the monthly salary for a domestic helper?', answer: 'According to Hong Kong government regulations, the 2025 minimum wage for domestic helpers is HK$5,195. Employers must also pay food allowance and provide free accommodation.' },
    { id: '5', category: 'process', question: 'How do I select a suitable helper?', answer: 'We will match the most suitable helper based on your family needs, including family members, living environment, and special requirements. You can learn about the helper through resumes, photos, and video interviews.' },
    { id: '6', category: 'process', question: 'Can I replace the helper if she is not suitable?', answer: 'Yes. We provide a two-year contract guarantee with free replacement service. If the helper\'s performance does not meet expectations during the probation period, we will arrange a free replacement.' },
    { id: '7', category: 'visa', question: 'How long does the visa application take?', answer: 'Immigration typically takes 4-6 weeks to process visa applications. Including consulate authentication time, the entire visa process takes about 6-8 weeks.' },
    { id: '8', category: 'insurance', question: 'What insurance is required for domestic helpers?', answer: 'By law, employers must purchase employee compensation insurance for helpers. We recommend also purchasing medical insurance to cover outpatient and hospitalization costs.' },
  ];

  useEffect(() => {
    fetchContent();
  }, [isEnglish]);

  const fetchContent = async () => {
    try {
      const response = await contentService.get();
      setContent(response.data);
      if (isEnglish) {
        if (response.data?.faqsEn?.length > 0) {
          setFaqs(response.data.faqsEn);
        } else {
          setFaqs(defaultFaqsEn);
        }
      } else {
        if (response.data?.faqs?.length > 0) {
          setFaqs(response.data.faqs);
        } else {
          setFaqs(defaultFaqs);
        }
      }
    } catch (error) {
      console.error('Error fetching FAQs:', error);
      setFaqs(isEnglish ? defaultFaqsEn : defaultFaqs);
    } finally {
      setLoading(false);
    }
  };

  const filteredFaqs = faqs.filter(faq => {
    const matchesSearch = faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         faq.answer.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = activeCategory === 'all' || faq.category === activeCategory;
    return matchesSearch && matchesCategory;
  });

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin" style={{ color: '#00b6a5' }} />
      </div>
    );
  }

  return (
    <div className="bg-white min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-teal-50 to-lime-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl font-bold mb-6">
              <span style={{ color: '#00b6a5' }}>{getText('常見問題', 'Frequently Asked Questions')}</span>
            </h1>
            <p className="text-xl text-gray-600">{getText('快速找到您需要的答案', 'Quickly find the answers you need')}</p>
          </div>
        </div>
      </section>

      {/* Search */}
      <section className="py-8 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <Input
              type="text"
              placeholder="搜尋問題..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 py-6 text-lg"
            />
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="pb-8 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-2 justify-center">
            {categories.map((category) => (
              <Button
                key={category.id}
                variant={activeCategory === category.id ? 'default' : 'outline'}
                onClick={() => setActiveCategory(category.id)}
                className="transition-all duration-300"
                style={{
                  backgroundColor: activeCategory === category.id ? '#00b6a5' : 'transparent',
                  borderColor: '#00b6a5',
                  color: activeCategory === category.id ? 'white' : '#00b6a5'
                }}
              >
                {category.label}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ List */}
      <section className="pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {filteredFaqs.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-xl text-gray-500">沒有找到相關問題</p>
              <p className="text-gray-400 mt-2">請嘗試其他搜尋詞或直接聯絡我們</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredFaqs.map((faq) => (
                <Card key={faq.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                  <button
                    onClick={() => toggleExpand(faq.id)}
                    className="w-full text-left"
                  >
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start">
                        <h3 className="text-lg font-semibold pr-4" style={{ color: '#00b6a5' }}>
                          {faq.question}
                        </h3>
                        {expandedId === faq.id ? (
                          <ChevronUp className="h-6 w-6 flex-shrink-0" style={{ color: '#00b6a5' }} />
                        ) : (
                          <ChevronDown className="h-6 w-6 flex-shrink-0" style={{ color: '#00b6a5' }} />
                        )}
                      </div>
                      {expandedId === faq.id && (
                        <div className="mt-4 pt-4 border-t">
                          <p className="text-gray-700 leading-relaxed whitespace-pre-line">{renderTextWithLinks(faq.answer)}</p>
                        </div>
                      )}
                    </CardContent>
                  </button>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Still Have Questions */}
      <section className="py-16" style={{ backgroundColor: '#f8fffe' }}>
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-4" style={{ color: '#00b6a5' }}>
            還有其他問題？
          </h2>
          <p className="text-lg text-gray-600 mb-6">
            我們的專業團隊隨時為您解答
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={`https://api.whatsapp.com/send?phone=${(content?.whatsapp || '85261871388').replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer">
              <Button size="lg" style={{ backgroundColor: '#00b6a5' }}>
                WhatsApp 查詢
              </Button>
            </a>
            <a href={`tel:${(content?.phone || '+852 6187 1388').replace(/\s/g, '')}`}>
              <Button size="lg" variant="outline" style={{ borderColor: '#00b6a5', color: '#00b6a5' }}>
                致電: {content?.phone || '+852 6187 1388'}
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Popular Topics */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4" style={{ color: '#00b6a5' }}>
              熱門話題
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="pt-6">
                <h3 className="text-xl font-bold mb-3" style={{ color: '#00b6a5' }}>
                  費用明細
                </h3>
                <p className="text-gray-600 mb-4">了解聘請外傭的各項費用及每月開支</p>
                <Button
                  variant="outline"
                  className="w-full"
                  style={{ borderColor: '#00b6a5', color: '#00b6a5' }}
                  onClick={() => setActiveCategory('cost')}
                >
                  查看相關問題
                </Button>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="pt-6">
                <h3 className="text-xl font-bold mb-3" style={{ color: '#00b6a5' }}>
                  申請流程
                </h3>
                <p className="text-gray-600 mb-4">了解從挑選到到港的完整流程</p>
                <Button
                  variant="outline"
                  className="w-full"
                  style={{ borderColor: '#00b6a5', color: '#00b6a5' }}
                  onClick={() => setActiveCategory('process')}
                >
                  查看相關問題
                </Button>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="pt-6">
                <h3 className="text-xl font-bold mb-3" style={{ color: '#00b6a5' }}>
                  簽證辦理
                </h3>
                <p className="text-gray-600 mb-4">了解簽證申請的時間及所需文件</p>
                <Button
                  variant="outline"
                  className="w-full"
                  style={{ borderColor: '#00b6a5', color: '#00b6a5' }}
                  onClick={() => setActiveCategory('visa')}
                >
                  查看相關問題
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQ;
